import {
  type MutationStatus,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { DefaultRootState } from 'react-redux';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { create } from 'zustand';

import {
  DtoV2ClassifyDocumentsClassifyDocumentsDetails,
  DtoV2DocumentAssociationSaveDocumentAssociationsRequestV2,
} from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { useUser } from '~/common/hooks/useAuthentication';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { getNextClassifyAlert } from '~/common/util/ruleUtils';
import {
  ClassifyRuleIds,
  INACTIVE_DOCUMENT_ERROR_PATTERN,
  INACTIVE_DOCUMENT_TOAST_MESSAGE,
} from '~/pages/ClassifyDocumentsV2/constants';
import genericPageKeys from '~/pages/Generic/keys';
import { CATEGORY_IDS } from '~/pages/Home/constants';
import { HomeActions, HomeThunks } from '~/pages/Home/redux';

import { classifyKeys, quickMatchDocumentsQueryOptions } from './queries';

export function useSaveDocumentAssociationsMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const store = useStore<DefaultRootState>();
  const snackbar = useSnackbar();

  return useMutation({
    mutationKey: ['classify', 'saveDocumentAssociations'],
    onMutate: ({ actionType }) => {
      if (actionType !== 'next') return undefined;

      // store the next doc before saving, because the save may reorder the alerts
      const {
        selectedAlert,
        navigationItems: { allAlerts },
      } = store.getState().home;
      const nextClassifyAlert = getNextClassifyAlert(allAlerts, selectedAlert);

      return {
        nextClassifyRuleReferenceId: nextClassifyAlert?.ruleReferenceId,
      };
    },
    mutationFn: async ({
      actionType,
      associationChanged,
      classificationChanged,
      data,
    }: {
      actionType: 'continue' | 'next';
      associationChanged: boolean;
      classificationChanged: boolean;
      data: Omit<
        DtoV2DocumentAssociationSaveDocumentAssociationsRequestV2,
        'obfuscatedLoanIdentifier'
      >;
    }) => {
      if (!associationChanged && !classificationChanged) {
        return {
          actionType,
          currentDocumentId: data.documentId,
          invalidate: false,
        };
      }

      const response =
        await typedApiClient.v2.documentAssociationSaveDocumentAssociations({
          obfuscatedLoanIdentifier,
          ...data,
        });

      return {
        actionType,
        response: response.data,
        currentDocumentId: data.documentId,
        invalidate: true,
      };
    },
    onSuccess: async (ctx, _variables, mutationContext) => {
      if (ctx.invalidate) {
        // Wait for fresh allAlerts before resolving the next destination,
        // otherwise classifyNextAlert can navigate off a stale alert list.
        await (dispatch(
          HomeThunks.getNavigationDetails(false, isUnderwriter),
        ) as unknown as Promise<void>);
        await queryClient.invalidateQueries({ queryKey: [classifyKeys.all] });
      }

      let hasBrokerActionableClassifyDocuments: boolean | undefined;

      if (!isUnderwriter) {
        const classifyDocuments = queryClient.getQueryData<
          DtoV2ClassifyDocumentsClassifyDocumentsDetails[]
        >([
          classifyKeys.all,
          classifyKeys.classifyDocuments,
          obfuscatedLoanIdentifier,
        ]);
        hasBrokerActionableClassifyDocuments = classifyDocuments?.some(
          (document) => document.brokerActionRequired === true,
        );
      }

      dispatch(
        HomeActions.classifyNextAlert(
          ctx.actionType,
          isUnderwriter,
          mutationContext?.nextClassifyRuleReferenceId,
          hasBrokerActionableClassifyDocuments,
        ),
      );
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const data = error.response?.data;
      const errorMessage = data?.detail ?? data?.message ?? '';

      if (INACTIVE_DOCUMENT_ERROR_PATTERN.test(errorMessage)) {
        snackbar({
          message: INACTIVE_DOCUMENT_TOAST_MESSAGE,
          severity: 'error',
          duration: 5000,
          anchorToActionBar: true,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: genericPageKeys.all });
    },
  });
}

// Tanstack query v5 has a useMutationState hook that will make this unnecessary
export const useRemoveDocumentMutationStore = create(() => ({
  status: 'idle' as MutationStatus,
}));

export function useRemoveDocumentMutation() {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const { obfuscatedLoanIdentifier } = useUrl();
  const { mutate: refresh } = useRefreshNavigationDetailsMutation();
  const { navigationItems, allRequiredRulesCompleted, loanLockInformation } =
    useSelector(({ home }) => home);

  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationKey: ['classify', 'removeDocument', obfuscatedLoanIdentifier],
    mutationFn: async (params: {
      documentId: number;
      documentTypeId: number;
    }) => {
      const { data } = await typedApiClient.v1.classifyDocumentsHideDocument({
        obfuscatedLoanIdentifier,
        documentId: params.documentId,
        documentTypeId: params.documentTypeId,
      });

      if (data.message?.length === 0) {
        data.message = undefined;
      }

      if (!data.success) {
        throw new Error(data.message, {
          cause: data,
        });
      }

      return data;
    },
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({ queryKey: [classifyKeys.all] });
      dispatch(HomeActions.classifyNextAlert('next'));

      // optimistic update
      dispatch(
        HomeActions.getNavigationDetailsSuccess({
          categories: navigationItems.categories.reduce((acc, cur) => {
            if (cur.navigationCategoryId === CATEGORY_IDS.CLASSIFY) {
              for (const children of cur.navigationGutterItems) {
                children.gutterItemChildren =
                  children.gutterItemChildren.filter(
                    (x) => !x.gutterItemId.endsWith(`-${params.documentId}`),
                  );
              }
            }

            return [...acc, cur];
          }, []),
          allAlerts: navigationItems.allAlerts.filter(
            (x) =>
              !(
                x.ruleId === ClassifyRuleIds.DocumentView &&
                x.gutterItemId.endsWith(`-${params.documentId}`)
              ),
          ),
          allRequiredRulesCompleted,
          loanLockInformation,
        }),
      );
      refresh();

      snackbar({
        message: data.message ?? 'Document removed successfully',
        severity: 'success',
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      snackbar({
        message:
          error instanceof AxiosError || !error.message
            ? 'Failed to remove document'
            : error.message,
        severity: 'error',
        duration: 5000,
      });
    },
  });

  useRemoveDocumentMutationStore.setState({ status: mutation.status });

  return mutation;
}

export function useRefreshNavigationDetailsMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    navigationItems: { allAlerts, categories },
    allRequiredRulesCompleted,
    loanLockInformation,
  } = useSelector(({ home }) => home);

  return useMutation({
    async mutationFn() {
      const { data } = await typedApiClient.v1.classifyDocumentsNavigationList(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
      );
      return data;
    },
    onSuccess(classifyTab) {
      dispatch(
        HomeActions.getNavigationDetailsSuccess({
          categories: [
            classifyTab,
            ...categories.filter(
              (x) =>
                x.navigationCategoryId !== classifyTab.navigationCategoryId,
            ),
          ],
          allAlerts: [
            ...classifyTab.navigationGutterItems.flatMap((x) =>
              x.gutterItemChildren.length ? x.gutterItemChildren : [x],
            ),
            ...allAlerts.filter(
              (x) => x.ruleCategoryId !== classifyTab.navigationCategoryId,
            ),
          ],
          allRequiredRulesCompleted,
          loanLockInformation,
        }),
      );
      dispatch(
        HomeActions.setSelectedCategory(classifyTab.navigationCategoryId),
      );
      queryClient.invalidateQueries({
        queryKey: quickMatchDocumentsQueryOptions(obfuscatedLoanIdentifier)
          .queryKey,
      });
    },
  });
}
